import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, MessageCircle, User, Crown, Flame, Play, Users as UsersIcon, Video, Radio, Sun, Moon, MoreHorizontal, X, Diamond, Calendar, MapPin, Trophy, Heart, Share2, DollarSign, Palette, Sparkles, Sparkle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { useAIFilter } from '@/context/AIFilterContext';

const FloatingDock = ({ user }) => {
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { hideAIContent, toggleAIFilter } = useAIFilter();
  const [showMore, setShowMore] = useState(false);
  
  const isSupremium = user?.subscription_tier === 'supremium' || user?.is_admin;
  const isPremium = ['pro', 'enterprise', 'supremium', 'enterprise_supremium'].includes(user?.subscription_tier) || user?.is_admin;
  const isBusiness = ['enterprise', 'supremium', 'enterprise_supremium', 'nonprofit_enterprise', 'nonprofit_supremium'].includes(user?.subscription_tier) || user?.is_admin;
  const isCreator = ['pro', 'enterprise', 'supremium', 'enterprise_supremium', 'nonprofit_enterprise', 'nonprofit_supremium'].includes(user?.subscription_tier) || user?.is_admin;

  // Primary nav items shown on all screens
  const primaryItems = [
    { icon: Home, label: 'Home', path: '/dashboard' },
    { icon: Flame, label: 'Explore', path: '/explore' },
    { icon: Palette, label: 'Create', path: '/creativity-zone' },
    { icon: Play, label: 'Videos', path: '/videos' },
    { icon: MessageCircle, label: 'Messages', path: '/messages' },
  ];

  // Secondary items hidden on mobile, shown in "More" menu
  const secondaryItems = [
    { icon: UsersIcon, label: 'Communities', path: '/communities' },
    { icon: MapPin, label: 'Locale', path: '/locale' },
    { icon: Trophy, label: 'Awards', path: '/awards' },
    ...(isPremium ? [
      { icon: Heart, label: 'Dating', path: '/dating', premium: true },
    ] : []),
    ...(isBusiness ? [
      { icon: Share2, label: 'HubPost', path: '/hubpost', business: true },
    ] : []),
    ...(isCreator ? [
      { icon: DollarSign, label: 'Earnings', path: '/creator', creator: true },
    ] : []),
    { icon: Video, label: 'Talkspace', path: '/talkspace' },
    { icon: Radio, label: 'Live', path: '/live' },
    ...(isSupremium ? [
      { icon: Diamond, label: 'Hub', path: '/hub', supremium: true },
      { icon: Calendar, label: 'Calendar', path: '/calendar', supremium: true },
    ] : []),
    { icon: Crown, label: 'Premium', path: '/pricing' },
  ];

  const NavLink = ({ item, className = '' }) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={() => setShowMore(false)}
        className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${
          isActive
            ? 'bg-gradient-to-br from-primary to-accent text-white shadow-lg'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
        } ${className}`}
        data-testid={`nav-${item.label.toLowerCase()}`}
        title={item.label}
      >
        <item.icon className="w-5 h-5" />
      </Link>
    );
  };

  return (
    <>
      {/* More Menu Overlay */}
      <AnimatePresence>
        {showMore && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 md:hidden"
          >
            <div className="bg-white/90 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl p-3 shadow-2xl border border-white/20 dark:border-white/10">
              <div className="grid grid-cols-4 gap-2">
                {secondaryItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setShowMore(false)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl transition-all ${
                      location.pathname === item.path
                        ? 'bg-gradient-to-br from-primary to-accent text-white'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                    }`}
                    data-testid={`nav-more-${item.label.toLowerCase()}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Dock */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-6 left-0 right-0 z-50 flex justify-center px-4"
      >
        <div className="bg-white/70 dark:bg-gray-900/80 backdrop-blur-xl rounded-full px-3 md:px-4 py-3 shadow-2xl border border-white/20 dark:border-white/10 flex items-center justify-center gap-1 md:gap-2">
          {/* Primary Items - Always visible */}
          {primaryItems.map((item) => (
            <NavLink key={item.path} item={item} />
          ))}

          {/* Secondary Items - Hidden on mobile, visible on md+ */}
          <div className="hidden md:flex items-center gap-2">
            {secondaryItems.map((item) => (
              <NavLink key={item.path} item={item} />
            ))}
          </div>

          {/* More Button - Only on mobile */}
          <button
            onClick={() => setShowMore(!showMore)}
            className={`p-3 rounded-full transition-all duration-300 hover:scale-110 md:hidden ${
              showMore
                ? 'bg-gradient-to-br from-primary to-accent text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
            }`}
            data-testid="nav-more"
            title="More"
          >
            {showMore ? <X className="w-5 h-5" /> : <MoreHorizontal className="w-5 h-5" />}
          </button>

          {/* Profile Link */}
          <Link
            to="/profile"
            className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${
              location.pathname.startsWith('/profile')
                ? 'bg-gradient-to-br from-primary to-accent text-white shadow-lg'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
            }`}
            data-testid="nav-profile"
            title="Profile"
          >
            <User className="w-5 h-5" />
          </Link>

          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          
          {/* AI Content Filter Toggle */}
          <button
            onClick={toggleAIFilter}
            className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${
              hideAIContent 
                ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg' 
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
            }`}
            data-testid="ai-filter-toggle"
            title={hideAIContent ? 'Show AI Content' : 'Hide AI Content'}
          >
            {hideAIContent ? <Sparkle className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
          </button>
          
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-3 rounded-full transition-all duration-300 hover:scale-110 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 theme-toggle"
            data-testid="theme-toggle"
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default FloatingDock;